import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface BookingMessage {
  id: string;
  type: number | 'CustomerMessage' | 'AdminReply' | 'InternalNote'; // API sends numbers (0,1,2)
  content: string;
  senderName: string;
  senderEmail?: string;
  createdAt: Date;
  isReadByAdmin: boolean;
  isReadByCustomer: boolean;
  attachments?: MessageAttachment[];
}

export interface MessageAttachment {
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType?: string;
  uploadedAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class MessageService {
  private apiUrl = '/api/bookings';
  private adminApiUrl = '/api/admin/bookings';

  constructor(private http: HttpClient) {}

  // Customer endpoints
  getMessages(bookingId: string, token: string): Observable<BookingMessage[]> {
    return this.http.get<BookingMessage[]>(`${this.apiUrl}/${bookingId}/messages`, {
      params: { token }
    });
  }

  addCustomerMessage(bookingId: string, token: string, content: string, files?: File[]): Observable<BookingMessage> {
    const formData = new FormData();
    formData.append('content', content);
    
    if (files) {
      files.forEach(file => formData.append('files', file, file.name));
    }

    return this.http.post<BookingMessage>(`${this.apiUrl}/${bookingId}/messages`, formData, {
      params: { token }
    });
  }

  markMessagesAsReadByCustomer(bookingId: string, token: string): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/${bookingId}/messages/mark-read`, {}, {
      params: { token }
    });
  }

  // Admin endpoints
  getAllMessages(bookingId: string): Observable<BookingMessage[]> {
    return this.http.get<BookingMessage[]>(`${this.adminApiUrl}/${bookingId}/messages`);
  }

  addAdminMessage(
    bookingId: string, 
    content: string, 
    isInternalNote: boolean = false,
    senderName?: string,
    senderEmail?: string,
    files?: File[]
  ): Observable<BookingMessage> {
    const formData = new FormData();
    formData.append('content', content);
    formData.append('isInternalNote', isInternalNote.toString());
    
    if (senderName) formData.append('senderName', senderName);
    if (senderEmail) formData.append('senderEmail', senderEmail);
    
    if (files) {
      files.forEach(file => formData.append('files', file, file.name));
    }

    return this.http.post<BookingMessage>(`${this.adminApiUrl}/${bookingId}/messages`, formData);
  }

  markMessagesAsReadByAdmin(bookingId: string): Observable<any> {
    return this.http.patch<any>(`${this.adminApiUrl}/${bookingId}/messages/mark-read`, {});
  }

  getUnreadMessagesCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.adminApiUrl.replace('/bookings', '')}/messages/unread-count`);
  }

  getBookingsWithUnreadMessages(): Observable<any[]> {
    return this.http.get<any[]>(`${this.adminApiUrl.replace('/bookings', '')}/bookings/with-unread-messages`);
  }
}
